import { useState, useEffect, useCallback } from "react"
import { useAuthentication } from "@src/auth"
import { usePartner } from "@src/partner"
import { getPartnerUuidRoles, PARTNER_USERS } from "@src/api"

interface UsePartnerAccessReturn {
  // State
  userRoles: PARTNER_USERS[]
  isIoTUser: boolean

  // Actions
  refreshData: () => void
}

export const usePartnerAccess = (): UsePartnerAccessReturn => {
  const { userEmail } = useAuthentication()
  const { partner } = usePartner()

  const [userRoles, setUserRoles] = useState<PARTNER_USERS[]>([])
  const [isIoTUser, setIsIoTUser] = useState<boolean>(false)

  // Check if user is IoT user
  useEffect(() => {
    setIsIoTUser(userEmail?.includes("iot-venture.com") ?? false)
  }, [userEmail])

  const refreshData = useCallback(async () => {
    if (!partner) return

    try {
      const { response: rolesResponse, data: rolesData } = await getPartnerUuidRoles(partner.uuid)
      if (rolesResponse.ok) {
        // When a non-IoT user is logged in, filter out IoT users from the displayed list
        if (!isIoTUser) {
          const filteredData = rolesData.map((item) => ({
            ...item,
            users: item.users.filter((user) => !user.email.includes("iot-venture.com")),
          }))
          setUserRoles(filteredData)
        } else {
          setUserRoles(rolesData)
        }
      }
    } catch (error) {
      // Error handling - could be improved with proper error reporting
    }
  }, [partner, isIoTUser])

  useEffect(() => {
    refreshData()
  }, [refreshData])

  return {
    userRoles,
    isIoTUser,
    refreshData,
  }
}
